import { createFormHook } from '@tanstack/react-form';
import { createElement } from 'react';

import PasswordInputMasking from '~/common/components/Inputs/PasswordInput/PasswordInputMasking';
import {
  fieldContext,
  formContext,
  useFieldContext,
} from '~/common/form/formContexts';
import CurrencyInput from '~/common/mui/Inputs/CurrencyInput';
import NumberInput from '~/common/mui/Inputs/NumberInput';
import PercentInput from '~/common/mui/Inputs/PercentInput';
import MultiSelect from '~/common/mui/Inputs/Selects/MultiSelect/MultiSelect';
import Select from '~/common/mui/Inputs/Selects/Select';
import TextField from '~/common/mui/Inputs/TextField';
import RadioGroup from '~/common/mui/Radios/MUIRadioGroup/RadioGroup';

type AppTextFieldProps = Omit<
  React.ComponentProps<typeof TextField>,
  'useTanstackForm' | 'useReactHooksForm'
>;

function AppTextField(props: AppTextFieldProps) {
  return createElement(TextField, { ...props, useTanstackForm: true });
}

type AppSelectProps = Omit<
  React.ComponentProps<typeof Select>,
  'useTanstackForm' | 'useReactHooksForm'
>;

function AppSelect(props: AppSelectProps) {
  return createElement(Select, { ...props, useTanstackForm: true });
}

type AppCurrencyInputProps = Omit<
  React.ComponentProps<typeof CurrencyInput>,
  'useTanstackForm' | 'useReactHooksForm'
>;

function AppCurrencyInput(props: AppCurrencyInputProps) {
  return createElement(CurrencyInput, { ...props, useTanstackForm: true });
}

type AppNumberInputProps = Omit<
  React.ComponentProps<typeof NumberInput>,
  'useTanstackForm' | 'useReactHooksForm'
>;

function AppNumberInput(props: AppNumberInputProps) {
  return createElement(NumberInput, { ...props, useTanstackForm: true });
}

type AppPercentInputProps = Omit<
  React.ComponentProps<typeof PercentInput>,
  'useTanstackForm' | 'useReactHooksForm'
>;

function AppPercentInput(props: AppPercentInputProps) {
  return createElement(PercentInput, { ...props, useTanstackForm: true });
}

type AppMultiSelectProps = Omit<
  React.ComponentProps<typeof MultiSelect>,
  'useTanstackForm'
>;

function AppMultiSelect(props: AppMultiSelectProps) {
  return createElement(MultiSelect, { ...props, useTanstackForm: true });
}

type AppRadioGroupProps = Omit<
  React.ComponentProps<typeof RadioGroup>,
  'useTanstackForm'
>;

function AppRadioGroup(props: AppRadioGroupProps) {
  return createElement(RadioGroup, { ...props, useTanstackForm: true });
}

type AppPasswordInputProps = Omit<
  React.ComponentProps<typeof PasswordInputMasking>,
  'value' | 'onChange' | 'error' | 'errorMessage'
>;

function AppPasswordInput(props: AppPasswordInputProps) {
  const field = useFieldContext<string>();
  return createElement(PasswordInputMasking, {
    ...props,
    value: field.state.value ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      field.handleChange(e.target.value),
    error: field.state.meta.errors.length > 0,
    errorMessage: field.state.meta.errors[0] as string | undefined,
  });
}

const { useAppForm, withForm, useTypedAppFormContext } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: {
    TextField: AppTextField,
    Select: AppSelect,
    CurrencyInput: AppCurrencyInput,
    NumberInput: AppNumberInput,
    PercentInput: AppPercentInput,
    MultiSelect: AppMultiSelect,
    RadioGroup: AppRadioGroup,
    PasswordInput: AppPasswordInput,
  },
  formComponents: {},
});

export const useFormContext = <
  TData extends Record<string, unknown> = Record<string, unknown>,
>() => useTypedAppFormContext({ defaultValues: {} as TData });

export { useAppForm, withForm };
